import React, { createContext, useState, useEffect, useContext } from 'react';
import PreferenceServ from '../service/PreferenceServ';
import { Spin } from 'antd';
import { translations as i18nData } from './translations';

const PreferenceContext = createContext();

export const PreferenceProvider = ({ children }) => {
    // Selected states initialized sync from localStorage to prevent UI flicker on refresh
    const [language, setLanguage] = useState(() => {
        const saved = localStorage.getItem('app_language_obj');
        if (saved) {
            try { return JSON.parse(saved); } catch (e) { }
        }
        return null;
    });

    const [currency, setCurrency] = useState(() => {
        const saved = localStorage.getItem('app_currency_obj');
        if (saved) {
            try { return JSON.parse(saved); } catch (e) { }
        }
        return null;
    });

    const [languages, setLanguages] = useState(() => {
        const saved = localStorage.getItem('app_languages_list');
        if (saved) {
            try { return JSON.parse(saved); } catch (e) { }
        }
        return [];
    });

    const [currencies, setCurrencies] = useState(() => {
        const saved = localStorage.getItem('app_currencies_list');
        if (saved) {
            try { return JSON.parse(saved); } catch (e) { }
        }
        return [];
    });

    const [loading, setLoading] = useState(true);



    // Load initial data
    useEffect(() => {
        const initPreferences = async () => {
            try {
                const [langRes, currRes] = await Promise.all([
                    PreferenceServ.getLanguages(),
                    PreferenceServ.getCurrencies()
                ]);

                const activeLangs = Array.isArray(langRes) ? langRes : (langRes.data || []);
                const activeCurrs = Array.isArray(currRes) ? currRes : (currRes.data || []);

                setLanguages(activeLangs);
                setCurrencies(activeCurrs);
                localStorage.setItem('app_languages_list', JSON.stringify(activeLangs));
                localStorage.setItem('app_currencies_list', JSON.stringify(activeCurrs));

                // --- LANGUAGE SETUP ---
                const savedLangObjStr = localStorage.getItem('app_language_obj');
                let savedLangObj = null;
                try {
                    if (savedLangObjStr) savedLangObj = JSON.parse(savedLangObjStr);
                } catch (e) { }

                let selectedLang = activeLangs.find(l => l.code === (savedLangObj?.code || localStorage.getItem('app_language')));

                if (!selectedLang && savedLangObj) {
                    selectedLang = savedLangObj;
                }

                if (!selectedLang) {
                    const browserLang = navigator.language.split('-')[0];
                    selectedLang = activeLangs.find(l => l.code === browserLang);
                }

                if (!selectedLang) {
                    selectedLang = activeLangs.find(l => l.isDefault) || activeLangs[0];
                }

                if (!selectedLang) selectedLang = { code: 'en', name: 'English', isDefault: true };

                setLanguage(selectedLang);
                localStorage.setItem('app_language', selectedLang.code);
                localStorage.setItem('app_language_obj', JSON.stringify(selectedLang));


                // --- CURRENCY SETUP ---
                const savedCurrObjStr = localStorage.getItem('app_currency_obj');
                let savedCurrObj = null;
                try {
                    if (savedCurrObjStr) savedCurrObj = JSON.parse(savedCurrObjStr);
                } catch (e) { }

                let selectedCurr = activeCurrs.find(c => c.code === (savedCurrObj?.code || localStorage.getItem('app_currency')));

                if (!selectedCurr && savedCurrObj) {
                    selectedCurr = savedCurrObj;
                }

                if (!selectedCurr) {
                    selectedCurr = activeCurrs.find(c => c.isDefault) || activeCurrs[0];
                }

                if (!selectedCurr) selectedCurr = { code: 'INR', symbol: '₹', exchangeRate: 1 };

                setCurrency(selectedCurr);
                localStorage.setItem('app_currency', selectedCurr.code);
                localStorage.setItem('app_currency_obj', JSON.stringify(selectedCurr));

            } catch (error) {
                console.error("Failed to load preferences:", error);
                if (!language) setLanguage({ code: 'en', name: 'English' });
                if (!currency) setCurrency({ code: 'INR', symbol: '₹', exchangeRate: 1 });
            } finally {
                setLoading(false);
            }
        };

        initPreferences();
    }, []);

    // Change Language
    const changeLanguage = (code) => {
        const lang = languages.find(l => l.code === code);
        if (lang) {
            setLanguage(lang);
            localStorage.setItem('app_language', code);
            localStorage.setItem('app_language_obj', JSON.stringify(lang));
        }
    };

    // Change Currency
    const changeCurrency = (code) => {
        const curr = currencies.find(c => c.code === code);
        if (curr) {
            setCurrency(curr);
            localStorage.setItem('app_currency', code);
            localStorage.setItem('app_currency_obj', JSON.stringify(curr));
        }
    };

    // Helper: Format Price
    const formatPrice = (amount) => {
        if (amount === undefined || amount === null) return '';
        if (!currency) return `₹${amount}`;

        // Convert amount
        // Assuming base price is always stored in BASE currency (e.g. INR is base 1)
        // If the currency has `exchangeRate`, we MULTIPLY (if base is 1) or DIVIDE logic depends on definition.
        // Usually: Display = Base * Rate.
        // Let's assume Rate is "How many Target Currency per 1 Base Currency"

        const converted = amount * (currency.exchangeRate || 1);

        return new Intl.NumberFormat(language?.code || 'en-US', {
            style: 'currency',
            currency: currency.code,
            minimumFractionDigits: 2
        }).format(converted);
    };

    // Helper: Translate
    const t = (key, fallback) => {
        const langCode = language?.code || localStorage.getItem('app_language') || 'en';
        // Fallback to English if key missing in target language, then to provided fallback, then to key
        return i18nData[langCode]?.[key] || i18nData['en']?.[key] || fallback || key;
    };

    const contextValue = React.useMemo(() => ({
        languages,
        currencies,
        language,
        currency,
        changeLanguage,
        changeCurrency,
        formatPrice,
        t
    }), [languages, currencies, language, currency]);

    if (loading) {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#fff'
            }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {[0, 1, 2].map((i) => (
                        <div key={i} style={{
                            animation: `bounce 0.6s infinite ease-in-out ${i * 0.15}s`,
                            opacity: i === 1 ? 1 : 0.4,
                        }}>
                            <div style={{ transform: i === 1 ? 'scale(1.2)' : 'scale(0.8)' }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2C12 2 13 4 13 5C13 6 12 7 12 7C12 7 11 6 11 5C11 4 12 2 12 2Z" fill="#16a34a" />
                                    <path d="M12 7C12 7 16 11 18 14C20 17 19 20 17 21C15 22 12 21 12 21C12 21 9 22 7 21C5 20 4 17 6 14C8 11 12 7 12 7Z" fill="#f97316" />
                                </svg>
                            </div>
                        </div>
                    ))}
                </div>
                <style>{`
                    @keyframes bounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-15px); }
                    }
                `}</style>
            </div>
        );
    }



    return (
        <PreferenceContext.Provider value={contextValue}>
            {children}
        </PreferenceContext.Provider>
    );
};

export const usePreference = () => useContext(PreferenceContext);
